import { useState, useEffect } from "react";
import Navbar from "../../components/feature/Navbar";
import Footer from "../../components/feature/Footer";
import { useNavigate } from "react-router-dom";
import { fetchNews, type NewsItem } from "../../api";

/* 兜底默认数据 */
const fallbackNews = [
  {
    id: "news-1",
    date: "May 20, 2026",
    category: "Product Launch",
    title: "Huatera Unveils HT-950 Ultra Series with 5G Integration",
    excerpt: "The next generation of professional two-way radios now features real-time cloud connectivity, enabling fleet managers to track and communicate with teams globally.",
    image: "https://readdy.ai/api/search-image?query=Modern%20professional%20walkie-talkie%20two-way%20radio%20device%20with%20digital%20display%20screen%20showing%20network%20connectivity%20status%20futuristic%20design%20blue%20and%20green%20accent%20lighting%20dark%20background%20product%20photography%20high%20quality&width=600&height=400&seq=110&orientation=squarish",
  },
  {
    id: "news-2",
    date: "May 15, 2026",
    category: "Partnership",
    title: "Huatera Partners with Global Logistics Leader for Fleet Deployment",
    excerpt: "A strategic partnership to equip over 5,000 vehicles across 12 countries with Huatera HT-700 Rugged radios for seamless logistics coordination.",
    image: "https://readdy.ai/api/search-image?query=Large%20logistics%20fleet%20trucks%20on%20highway%20with%20modern%20warehouse%20in%20background%20professional%20transportation%20industry%20blue%20and%20green%20color%20tones%20professional%20photography%20wide%20angle&width=600&height=400&seq=111&orientation=squarish",
  },
  {
    id: "news-3",
    date: "May 10, 2026",
    category: "Technology",
    title: "AI-Powered Voice Enhancement Technology Reaches 99% Accuracy",
    excerpt: "Our latest noise cancellation algorithm now filters out construction noise, wind, and engine sounds with unprecedented precision.",
    image: "https://readdy.ai/api/search-image?query=Artificial%20intelligence%20voice%20technology%20concept%20sound%20waves%20and%20neural%20network%20visualization%20modern%20digital%20illustration%20blue%20and%20green%20color%20scheme%20clean%20minimal%20technology%20background&width=600&height=400&seq=112&orientation=squarish",
  },
  {
    id: "news-4",
    date: "May 05, 2026",
    category: "Awards",
    title: 'Huatera Wins "Best Communication Device" at TechExpo 2026',
    excerpt: "The HT-900 Pro has been recognized as the top professional two-way radio by industry experts at this year's international technology exhibition.",
    image: "https://readdy.ai/api/search-image?query=Modern%20technology%20award%20ceremony%20trophy%20on%20stage%20with%20blue%20and%20green%20lighting%20professional%20event%20photography%20high%20quality%20celebration%20atmosphere&width=600&height=400&seq=113&orientation=squarish",
  },
  {
    id: "news-5",
    date: "April 28, 2026",
    category: "Expansion",
    title: "Huatera Opens New European Headquarters in Amsterdam",
    excerpt: "To better serve our growing European client base, we have established a new regional office and distribution center in the Netherlands.",
    image: "https://readdy.ai/api/search-image?query=Modern%20European%20office%20building%20exterior%20in%20Amsterdam%20style%20canal%20view%20professional%20architecture%20photography%20blue%20sky%20green%20trees%20professional%20corporate%20headquarters&width=600&height=400&seq=114&orientation=squarish",
  },
  {
    id: "news-6",
    date: "April 22, 2026",
    category: "Product Launch",
    title: "New HT-400 Lite Series for Small Business Teams",
    excerpt: "An affordable yet powerful communication solution designed for retail stores, restaurants, and small event teams with up to 15km range.",
    image: "https://readdy.ai/api/search-image?query=Compact%20lightweight%20walkie-talkie%20two-way%20radio%20device%20modern%20design%20with%20clip%20small%20business%20retail%20use%20clean%20background%20product%20photography%20blue%20and%20green%20accent%20colors&width=600&height=400&seq=115&orientation=squarish",
  },
];

const fallbackCategories = ["All"];

export default function News() {
  const navigate = useNavigate();
  const [newsItems, setNewsItems] = useState<NewsItem[]>(fallbackNews);
  const [categories, setCategories] = useState<string[]>(fallbackCategories);

  useEffect(() => {
    fetchNews()
      .then((res) => {
        setNewsItems(res.items.length > 0 ? res.items : fallbackNews);
        setCategories(
          res.categories.length > 0
            ? ["All", ...res.categories.map((c) => c.name)]
            : fallbackCategories
        );
      })
      .catch((err) => console.warn("新闻数据加载失败:", err));
  }, []);

  // 格式化日期
  const formatDate = (dateStr: string) => {
    try {
      return new Date(dateStr).toLocaleDateString("en-US", {
        year: "numeric",
        month: "long",
        day: "numeric",
      });
    } catch {
      return dateStr;
    }
  };

  return (
    <div className="min-h-screen bg-white">
      <Navbar />
      <main>
        {/* Hero */}
        <section className="relative w-full h-[400px] md:h-[500px] overflow-hidden">
          <div className="absolute inset-0">
            <img
              src="https://readdy.ai/api/search-image?query=Professional%20modern%20conference%20room%20with%20large%20screen%20displaying%20data%20analytics%20business%20meeting%20table%20with%20microphones%20and%20communication%20equipment%20blue%20and%20green%20ambient%20lighting%20corporate%20photography%20wide%20angle%20high%20quality&width=1400&height=800&seq=116&orientation=landscape"
              alt="News"
              className="w-full h-full object-cover object-top"
            />
            <div className="absolute inset-0 bg-gradient-to-r from-dark/60 to-dark/30"></div>
          </div>
          <div className="relative z-10 h-full flex flex-col justify-center px-4 md:px-8 lg:px-12">
            <div className="max-w-4xl">
              <div className="inline-flex items-center px-4 py-1.5 bg-white/20 rounded-full mb-6">
                <span className="text-white text-sm font-medium">Latest News</span>
              </div>
              <h1 className="text-4xl md:text-6xl font-bold text-white leading-tight">
                News & Updates
              </h1>
              <p className="text-white/80 text-lg mt-4 max-w-2xl">
                Stay informed about our latest product launches, partnerships, and industry innovations.
              </p>
            </div>
          </div>
        </section>

        {/* Category Filter */}
        <section className="py-8 px-4 md:px-8 lg:px-12 border-b border-gray-100">
          <div className="max-w-6xl mx-auto flex flex-wrap items-center gap-3">
            <span className="text-gray-500 text-sm mr-2">Filter:</span>
            {categories.map((cat, i) => (
              <button
                key={cat}
                className={`px-4 py-2 rounded-full text-sm font-medium transition-colors ${
                  i === 0
                    ? "bg-primary text-white"
                    : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                }`}
              >
                {cat}
              </button>
            ))}
          </div>
        </section>

        {/* News Grid */}
        <section className="py-12 md:py-20 px-4 md:px-8 lg:px-12">
          <div className="max-w-6xl mx-auto">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {newsItems.map((item) => (
                <article
                  key={item.id}
                  className="group bg-white rounded-2xl overflow-hidden border border-gray-100 hover:border-primary/30 hover:shadow-lg transition-all duration-300 cursor-pointer"
                >
                  <div className="aspect-[4/3] overflow-hidden">
                    <img
                      src={item.image}
                      alt={item.title}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                    />
                  </div>
                  <div className="p-5">
                    <div className="flex items-center gap-3 mb-3">
                      <span className="px-3 py-1 bg-primary/10 text-primary text-xs font-medium rounded-full">
                        {item.category}
                      </span>
                      <span className="text-gray-400 text-xs">{formatDate(item.news_date)}</span>
                    </div>
                    <h3 className="text-dark font-bold text-base leading-snug mb-2 group-hover:text-primary transition-colors">
                      {item.title}
                    </h3>
                    <p className="text-gray-500 text-sm leading-relaxed line-clamp-3">
                      {item.excerpt}
                    </p>
                    <div className="mt-4 flex items-center gap-2 text-primary text-sm font-medium">
                      <span>Read More</span>
                      <i className="ri-arrow-right-line"></i>
                    </div>
                  </div>
                </article>
              ))}
            </div>
          </div>
        </section>

        {/* Newsletter CTA */}
        <section className="bg-light-blue py-16 md:py-20 px-4 md:px-8 lg:px-12">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="text-3xl md:text-4xl font-bold text-dark mb-4">
              Stay in the Loop
            </h2>
            <p className="text-gray-600 mb-8 max-w-xl mx-auto">
              Subscribe to our newsletter and be the first to know about new products, exclusive offers, and industry insights.
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-3 max-w-md mx-auto">
              <input
                type="email"
                placeholder="Enter your email"
                className="w-full sm:flex-1 px-4 py-3 bg-white rounded-lg border border-gray-200 text-sm focus:border-primary focus:outline-none transition-colors"
              />
              <button className="w-full sm:w-auto px-6 py-3 bg-primary text-white font-semibold rounded-lg hover:bg-primary-dark transition-colors whitespace-nowrap">
                Subscribe
              </button>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
}